import type { ProjectRu } from '../i18n';

/** Russian overlays for the project registry, keyed by slug. Missing fields
 *  fall back to English. Brand names (StudyNinja, KnowledgeBase AI, Agent
 *  Native Universe, MagicBrain, Balansis, UWT) stay in Latin. */
export const projectsRu: Record<string, ProjectRu> = {
  studyninja: {
    tagline: 'Адаптивная платформа обучения',
    statusLabel: 'В продакшене',
    domains: ['AI', 'Образование', 'Графы знаний'],
    summary:
      'Платформа обучения для школьников: граф знаний по предмету, диагностика пробелов и выбор следующего шага. Здесь исследования лаборатории встречаются с реальными пользователями, а не с демо.',
    body: [
      'StudyNinja — испытательный полигон лаборатории. Каждый слой наших исследований — моделирование знаний, оценка усвоения, адаптивная последовательность заданий — выкатывается сюда первым и оценивается по единственной метрике: сумел ли отстающий ученик вернуться на свою траекторию.',
      'Платформа ведёт граф знаний по каждому предмету, определяет, где именно застрял ученик, и подбирает наименьший следующий шаг, дающий заметный результат. Мотивация рождается из успеха, а не выдаётся авансом — продукт спроектирован вокруг этого принципа.',
      'StudyNinja работает в продакшене с живыми пользователями, и это держит исследования честными: модель, которая работает только на бенчмарке, здесь не выживает.',
    ],
  },
  'knowledgebase-ai': {
    tagline: 'Платформа знаний',
    statusLabel: 'В продакшене',
    domains: ['Графы знаний', 'Семантический поиск', 'AI'],
    summary:
      'Движок, превращающий документы и предметную область в связную модель: сущности, связи, семантический поиск и происхождение знаний. Живёт отдельно от любого конкретного продукта и переиспользуется во всех проектах.',
    body: [
      'KnowledgeBase AI — слой знаний лаборатории в виде самостоятельной системы. Он принимает документы и структуру предметной области и строит связную модель: сущности, связи, семантический поиск и происхождение каждого фрагмента знаний.',
      'Отделённость от любого конкретного продукта сделана намеренно: один и тот же движок обслуживает учебные сценарии в StudyNinja и внутренние исследовательские инструменты, и это заставляет абстракции оставаться общими.',
    ],
  },
  'agent-native-universe': {
    tagline: 'Среда исполнения агентов с открытым кодом',
    statusLabel: 'Открытый код · MIT',
    domains: ['Мультиагентные системы', 'Распределённые системы', 'Открытый код'],
    summary:
      'Среда исполнения для самоорганизующихся агентов: согласование отношений, экономика ресурсов, византийское согласие между узлами и рекурсивная организация в группы. Под лицензией MIT — скачайте и проверьте.',
    body: [
      'Agent Native Universe (ANU) исследует, как выглядит ПО, когда его пользователи первого класса — агенты, а не люди. Вместо оркестратора, раздающего задачи, агенты ANU сами согласуют отношения друг с другом, платят за ресурсы во встроенной экономике и достигают византийского согласия без центрального арбитра.',
      'Группы агентов могут сворачиваться в один составной агент и разворачиваться обратно — организация становится рекурсивной, а структура рождается из взаимодействия, а не из конфигурации.',
      'ANU — публичный исследовательский артефакт лаборатории: выпущен под MIT, версионируется и открыт для изучения. Заодно он служит зафиксированным уровнем техники (prior art) для агент-native направления исследований.',
    ],
  },
  magicbrain: {
    tagline: 'Нейронное исследовательское ядро',
    statusLabel: 'Исследование',
    domains: ['Импульсные сети', 'Локальное обучение', 'Нейроморфный AI'],
    summary:
      'Исследовательское ядро: импульсные сети, локальное обучение, структура, которая выращивается, а не проектируется вручную. Часть механизмов не опубликована, поэтому публично мы говорим о назначении и наблюдаемых свойствах.',
    body: [
      'MagicBrain — собственное направление лаборатории в области вычислений, подобных работе мозга: импульсные сети, правила обучения, локальные для отдельной связи, и структура сети, которая развивается, а не проектируется вручную.',
      'Несколько его механизмов не опубликованы. Пока это так, публичное описание намеренно ограничено назначением и наблюдаемым поведением — эту границу мы обозначаем открыто, а не скрываем.',
    ],
  },
  balansis: {
    tagline: 'Численный фундамент',
    statusLabel: 'Открытый код · AGPL-3.0',
    domains: ['Численные вычисления', 'Надёжность', 'Открытый код'],
    summary:
      'Вычислительная библиотека, спроектированная так, чтобы деление на ноль, переполнение и потеря точности не рушили весь конвейер. Фундамент под остальными слоями — опубликована на GitHub и устанавливается из PyPI.',
    body: [
      'Balansis задаёт простой вопрос с дорогими последствиями: почему всё вычисление должно падать из-за того, что одно значение попало в особую точку? Библиотека построена так, чтобы деление на ноль, переполнение и накопленная потеря точности деградировали мягко, а не уничтожали результат.',
      'Она лежит под остальными слоями стека как их численный фундамент. В отличие от исследовательских слоёв, она уже открыта: исходный код на GitHub, релизы на PyPI, а сам механизм — в коде: его можно прочитать, а не принимать гарантии на веру.',
    ],
  },
  uwt: {
    tagline: 'Теоретический исследовательский трек',
    statusLabel: 'Исследование',
    domains: ['Теория', 'Научный метод'],
    summary:
      'Отдельное направление исследований с реестром экспериментов, тестами и подготовленным пакетом препринта. Оно задаёт планку: утверждение, которое нельзя проверить, не выходит наружу.',
    body: [
      'UWT (Unified Wholeness Theory) — теоретический трек лаборатории, который ведётся с дисциплиной экспериментального: реестр экспериментов, автоматические тесты по утверждениям и пакет препринта, подготовленный к публикации.',
      'Его роль внутри лаборатории столь же культурная, сколь и научная — он задаёт планку, при которой непроверяемое утверждение не покидает стен лаборатории.',
    ],
  },
};
